import {
    SHOW_UNSUPPORTED_ONSA_PRODUCTS,
    ONSA_PILOT_ENABLED,
    ONSA_PILOT_LOBS
} from '../../config/onsaConfig';

type ProductKey =
    | 'privateContent'
    | 'youthContent'
    | 'travel'
    | 'car'
    | 'accident'
    | 'youngAccident'
    | 'childAccident'
    | 'dog'
    | 'cat'
    | 'house'
    | 'holidayHome'
    | 'salaryProtection'
    | 'horse'
    | 'singleTripTravel'
    | 'singleTripTravelCancellation'
    | 'motorcycle'
    | 'boat'
    | 'moped'
    | 'caravan'
    | 'trailer'
    | 'changeOfOwnership';

interface Category {
    label: string;
    iconShape: string;
    products: ProductKey[];
}

interface Product {
    title: string;
    iconShape: string;
    supported: boolean;
    quantitySelector?: boolean;
}

type ProductCollection = Record<ProductKey, Product>;

const products: ProductCollection = {
    house: {
        title: 'Husforsikring',
        iconShape: 'house-sun',
        // TODO: sepah team must reenable this when ASO has solved BUG828642 and the related bugs
        // quantitySelector: true,
        supported: true
    },
    holidayHome: {
        title: 'Fritidshusforsikring',
        iconShape: 'house-vacation',
        // TODO: sepah team must reenable this when ASO has solved BUG828642 and the related bugs
        // quantitySelector: true,
        supported: true
    },
    privateContent: {
        title: 'Indboforsikring',
        iconShape: 'furniture',
        supported: true
    },
    youthContent: {
        title: 'Indboforsikring til unge',
        iconShape: 'furniture',
        supported: true
    },
    travel: {
        title: 'Rejseforsikring',
        iconShape: 'plane',
        supported: true
    },
    car: {
        title: 'Bilforsikring',
        iconShape: 'car-front',
        supported: true,
        quantitySelector: true
    },
    accident: {
        title: 'Ulykkesforsikring',
        iconShape: 'broken-leg',
        quantitySelector: true,
        supported: true
    },
    youngAccident: {
        title: 'Ulykkesforsikring til unge',
        iconShape: 'person-girl',
        quantitySelector: true,
        supported: true
    },
    childAccident: {
        title: 'Børneforsikring',
        iconShape: 'boy-girl',
        quantitySelector: true,
        supported: true
    },
    dog: {
        title: 'Hundeforsikring',
        iconShape: 'dog',
        quantitySelector: true,
        supported: true
    },
    cat: {
        title: 'Katteforsikring',
        iconShape: 'cat',
        quantitySelector: true,
        supported: true
    },
    horse: {
        title: 'Hesteforsikring',
        iconShape: 'horse-02',
        quantitySelector: true,
        supported: false
    },
    salaryProtection: {
        title: 'Lønsikring',
        iconShape: 'money-stack',
        supported: false
    },
    singleTripTravel: {
        title: 'Enkeltrejseforsikring',
        iconShape: 'plane',
        supported: false
    },
    singleTripTravelCancellation: {
        title: 'Afbestilling af enkeltrejse',
        iconShape: 'ticket',
        supported: false
    },
    motorcycle: {
        title: 'Motorcykelforsikring',
        iconShape: 'motorbike',
        supported: false
    },
    boat: {
        title: 'Bådforsikring',
        iconShape: 'boat',
        supported: false
    },
    moped: {
        title: 'Knallertforsikring',
        iconShape: 'scooter',
        supported: false
    },
    caravan: {
        title: 'Campingvognsforsikring',
        iconShape: 'caravan',
        quantitySelector: true,
        supported: true
    },
    trailer: {
        title: 'Påhængsvognsforsikring',
        iconShape: 'trailer',
        quantitySelector: true,
        supported: true
    },
    changeOfOwnership: {
        title: 'Ejerskifteforsikring',
        iconShape: 'house-sun',
        supported: false
    }
};

const categories: Category[] = [
    {
        label: 'Mest Populære',
        iconShape: 'star',
        products: ONSA_PILOT_ENABLED
            ? [
                  'privateContent',
                  'youthContent',
                  'travel',
                  'accident',
                  'youngAccident',
                  'childAccident',
                  'dog',
                  'cat'
              ]
            : [
                  'privateContent',
                  'youthContent',
                  'travel',
                  'car',
                  'accident',
                  'youngAccident',
                  'childAccident',
                  'dog'
              ]
    },
    {
        label: 'Hus & hjem',
        iconShape: 'house-sun',
        products: ['house', 'holidayHome', 'privateContent', 'youthContent', 'changeOfOwnership']
    },
    {
        label: 'Transport',
        iconShape: 'car-van-side',
        products: ['car', 'motorcycle', 'boat', 'moped', 'caravan', 'trailer']
    },
    {
        label: 'Rejse & fritid',
        iconShape: 'plane',
        products: [
            'holidayHome',
            'travel',
            'singleTripTravel',
            'singleTripTravelCancellation',
            'boat',
            'caravan'
        ]
    },
    {
        label: 'Helbred & Job',
        iconShape: 'health-cross',
        products: ['accident', 'youngAccident', 'childAccident', 'salaryProtection']
    },
    {
        label: 'Dyr',
        iconShape: 'dog',
        products: ['dog', 'cat', 'horse']
    },
    {
        label: 'Børn & unge',
        iconShape: 'person-boy',
        products: ['youthContent', 'youngAccident', 'childAccident']
    },
    {
        label: 'Alle',
        iconShape: 'circle-plus',
        products: [
            'house',
            'holidayHome',
            'privateContent',
            'youthContent',
            'travel',
            'car',
            'accident',
            'youngAccident',
            'childAccident',
            'salaryProtection',
            'dog',
            'cat',
            'horse',
            'singleTripTravel',
            'singleTripTravelCancellation',
            'motorcycle',
            'boat',
            'moped',
            'caravan',
            'trailer',
            'changeOfOwnership'
        ]
    }
]
    .map((category) => ({
        ...category,
        products: category.products
            .filter(
                (product) =>
                    SHOW_UNSUPPORTED_ONSA_PRODUCTS || products[product as ProductKey]?.supported
            )
            .filter(
                (product) => !ONSA_PILOT_ENABLED || ONSA_PILOT_LOBS.includes(product)
            ) as ProductKey[]
    }))
    .filter((category) => category.products.length > 0);

export { categories, products, type Category, type ProductKey };
